import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, inject } from '@angular/core';

@Component({
  selector: 'app-table-pagination',
  templateUrl: './table-pagination.component.html',
  styleUrl: './table-pagination.component.scss',
  imports: [CommonModule],
})
export class TablePaginationComponent {
  @Input() page = 1;
  @Input() limit = 50;
  @Input() total = 0;
  @Input() totalPage = 1;
  @Input() dataLength = 0;
  @Input() offset = 0;
  @Input() isLoading = false;
  @Input() hideWhileInitialLoading = false;
  @Input() pageSizeOptions: number[] = [10, 20, 50];
  @Input() ariaLabel = 'Table pagination';

  @Output() pageChange = new EventEmitter<number>();
  @Output() limitChange = new EventEmitter<number>();

  isPageSizeOpen = false;

  private elementRef = inject(ElementRef<HTMLElement>);

  get normalizedPage(): number {
    return Number.isFinite(this.page) && this.page > 0 ? this.page : 1;
  }

  get normalizedTotalPage(): number {
    return Number.isFinite(this.totalPage) && this.totalPage > 0 ? this.totalPage : 1;
  }

  get normalizedTotal(): number {
    return Number.isFinite(this.total) && this.total > 0 ? this.total : 0;
  }

  get showingStart(): number {
    return this.normalizedTotal === 0 ? 0 : this.normalizedOffset + 1;
  }

  get showingEnd(): number {
    if (this.normalizedTotal === 0) {
      return 0;
    }

    return Math.min(this.normalizedOffset + this.normalizedDataLength, this.normalizedTotal);
  }

  get isFirstPage(): boolean {
    return this.normalizedPage <= 1;
  }

  get isLastPage(): boolean {
    return this.normalizedPage >= this.normalizedTotalPage;
  }

  get shouldHideInitialLoadingState(): boolean {
    return this.hideWhileInitialLoading &&
      this.isLoading &&
      this.normalizedTotal === 0 &&
      this.normalizedDataLength === 0;
  }

  private get normalizedOffset(): number {
    return Number.isFinite(this.offset) && this.offset > 0 ? this.offset : 0;
  }

  private get normalizedDataLength(): number {
    return Number.isFinite(this.dataLength) && this.dataLength > 0 ? this.dataLength : 0;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;

    if (target === null || this.elementRef.nativeElement.contains(target) === false) {
      this.closePageSizeDropdown();
    }
  }

  onLimitChange(value: number | string | null): void {
    const normalizedValue = Number(value);

    if (!Number.isFinite(normalizedValue) || normalizedValue === this.limit) {
      this.closePageSizeDropdown();
      return;
    }

    this.closePageSizeDropdown();
    this.limitChange.emit(normalizedValue);
  }

  togglePageSizeDropdown(event?: Event): void {
    event?.stopPropagation();
    this.isPageSizeOpen = !this.isPageSizeOpen;
  }

  selectPageSize(option: number, event?: Event): void {
    event?.stopPropagation();
    this.onLimitChange(option);
  }

  closePageSizeDropdown(): void {
    this.isPageSizeOpen = false;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.normalizedTotalPage || page === this.normalizedPage) {
      return;
    }

    this.pageChange.emit(page);
  }
}
